import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Colors,
  Icons,
  Typography,
  createClasses,
  spacing,
} from '@kp/react-ui';

import { KpiCard, type KpiCardProps } from './KpiCard';
import { KpiCardHeader } from './KpiCardHeader';

const classes = createClasses({
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing('xs'),
  },
  inactive: {
    color: Colors.Neutral.textInactive,
  },
});

export interface EnergyConsumptionCardProps
  extends Pick<
    KpiCardProps,
    'href' | 'isLoading' | 'isError' | 'isEmpty' | 'onRetry' | 'className'
  > {
  value: string;
  unit: string;
  trendPercent?: number | null;
  cost?: string | null;
  periodLabel: string;
}

export const EnergyConsumptionCard: React.FC<EnergyConsumptionCardProps> = ({
  value,
  unit,
  trendPercent = null,
  cost = null,
  periodLabel,
  ...cardProps
}) => {
  const { t, i18n } = useTranslation();
  const title = t('home.energy.title');

  const renderTrend = () => {
    if (trendPercent === null) return null;
    const percent = Math.abs(trendPercent).toLocaleString(i18n.language, {
      maximumFractionDigits: 1,
    });
    const up = trendPercent > 0;
    const trendColor = up ? 'red' : 'green';
    const color = trendPercent === 0 ? 'grey' : trendColor;

    return (
      <Badge
        color={color}
        size="tiny"
        aria-label={t('home.energy.trendLabel', { percent })}
      >
        {trendPercent !== 0 && (
          <Badge.Icon
            icon={up ? Icons.IconArrowUpward : Icons.IconArrowDownward}
          />
        )}
        <Badge.Title>{`${percent}%`}</Badge.Title>
      </Badge>
    );
  };

  return (
    <KpiCard
      header={
        <KpiCardHeader
          icon={Icons.IconBolt}
          title={title}
          subtitle={t('home.energy.subtitle')}
        />
      }
      linkLabel={t('home.openIn', { section: title })}
      {...cardProps}
    >
      <div className={classes.valueRow}>
        <Typography variant="headlineMedium">{value}</Typography>
        <Typography variant="bodySmall" className={classes.inactive}>
          {unit}
        </Typography>
        {renderTrend()}
      </div>
      {cost && (
        <Typography variant="bodySmall" className={classes.inactive}>
          {t('home.energy.cost', { cost, period: periodLabel })}
        </Typography>
      )}
    </KpiCard>
  );
};
